/**
 * List of all click macros available in adtron adtag-generator
 */

import { ClickMacroConfig } from "./types/models/adtag"

interface ClickMacroItem {
  name: string;
  value: string;
  toClickURL?: boolean;
  metadata?: Record<string, string>;
  gdprConsentMacro?: string;
  gdprMacro?: string;
}

const MACRO_LIST: ClickMacroItem[] = [
  { name: "Empty", value: "" },
  { name: "Google Ad Manager", value: "%%CLICK_URL_UNESC%%" },
  { name: "SmartAdServer", value: "[countgo]" },
  { name: "Adition", value: "%clickurl%" },
  { name: "Adtech/Helios", value: "_ADCLICK_" },
  { name: "DoubleClick/DART", value: "%c" },
  { name: "OpenX", value: "{clickurl}" },
  {
    name: "Splicky",
    value: "(S_CLICK_URL)&url=",
    gdprConsentMacro: "(S_TCF2_CONSENTSTRING)",
    gdprMacro: "(S_GDPR_APPLIES)",
    metadata: { sourceURL: "(S_SOURCE)" }
  },
  {
    name: "KayZen",
    value: "{CLICK_URL}",
    metadata: {
      bundleId: "{APP_BUNDLE}",
      sourceURL: "{APP_DOMAIN}"
    }
  },
  {
    name: "KayZen (managed)",
    value: "{HTML_CLICK_URL}",
    toClickURL: true,
    metadata: {
      bundleId: "{APP_BUNDLE}",
      sourceURL: "{APP_DOMAIN}"
    }
  },
  {
    name: "DV360",
    // eslint-disable-next-line no-template-curly-in-string
    value: "${CLICK_URL}",
    metadata: {
      // eslint-disable-next-line no-template-curly-in-string
      exchangeId: "${EXCHANGE_ID}",
      // eslint-disable-next-line no-template-curly-in-string
      bundleId: "${BUNDLE_ID}",
      // eslint-disable-next-line no-template-curly-in-string
      sourceURL: "${SOURCE_URL}"
    }
  },
  // eslint-disable-next-line no-template-curly-in-string
  { name: "AppNexus/Xandr", value: "${CLICK_URL}" },
  {
    name: "TheTradeDesk",
    value: "%%TTD_CLK%%",
    metadata: { supplyVendor: "%%TTD_SUPPLYVENDOR%%", sourceURL: "%%TTD_SITE%%" },
    gdprConsentMacro: "%%TTD_GDPR_CONSENT_STRING%%",
    gdprMacro: "%%TTD_GDPR_APPLIES%%"
  },
  {
    name: "Adform",
    value: "%%c1;cpdir=",
    gdprConsentMacro: "__ADFGPPSTRING__",
    gdprMacro: "__ADFGDPR__",
    metadata: { sourceURL: "__ADFBNRDOM__" }
  },
  { name: "Custom", value: "" }
]

/**
 * Manages a list of available click macros
 */
export default class ClickMacro {
  /**
   * Get all macro names
   * @returns Array of macro names
   */
  static getNames (): string[] {
    return MACRO_LIST.map(item => item.name)
  }

  /**
   * Get the complete list of macros
   * @returns The full macro list
   */
  static getList (): ClickMacroItem[] {
    return MACRO_LIST
  }

  /**
   * Get a macro value by its name
   * @param name - The name of the macro
   * @returns The macro value or null if not found
   */
  static getValueByName (name: string): string | null {
    const item = ClickMacro.getItemByConfig({ name, customClickMacro: null })
    return item ? item.value : null
  }

  /**
   * Get a macro value by configuration
   * @param config - Configuration object
   * @returns The macro value
   */
  static getValueByConfig (config: ClickMacroConfig): string | null {
    if (config.name === "Custom" && config.customClickMacro) {
      return config.customClickMacro
    }
    return ClickMacro.getValueByName(config.name)
  }

  /**
   * Get a macro item by configuration
   * @param config - Configuration object
   * @returns The macro item or undefined if not found
   */
  static getItemByConfig (config: ClickMacroConfig): ClickMacroItem | undefined {
    const item = MACRO_LIST.find(value =>
      value.name.toLowerCase() === config.name.toLowerCase()
    )

    if (item && config.name === "Custom" && config.customClickMacro) {
      item.value = config.customClickMacro
    }

    return item
  }
}
